import React, { useState, useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import { Card, CardHeader, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import SafeIcon from '../common/SafeIcon';
import { motion } from 'framer-motion';

export const Benchmark = () => {
  const [industry, setIndustry] = useState('Teknologi');
  const [size, setSize] = useState('Mellomstor (50-249)');
  
  // Selskapets faktiske score (fra Dashboard)
  const companyScores = {
    psychSafety: 42,
    stressControl: 35, // 100 - stress score (78)
    conflictRisk: 45,  // Invertert for sammenligning
    leadership: 48,
    overall: 54
  };

  // Benchmark data basert på bransje
  const benchmarks = {
    'Teknologi': { psychSafety: 72, stressControl: 65, conflictRisk: 70, leadership: 68, overall: 70 },
    'Helse': { psychSafety: 68, stressControl: 45, conflictRisk: 55, leadership: 75, overall: 62 },
    'Finans': { psychSafety: 65, stressControl: 55, conflictRisk: 75, leadership: 72, overall: 68 },
    'Offentlig': { psychSafety: 60, stressControl: 50, conflictRisk: 60, leadership: 65, overall: 58 }
  };

  const currentBenchmark = benchmarks[industry] || benchmarks['Teknologi'];
  const nationalAverage = { psychSafety: 65, stressControl: 58, conflictRisk: 64, leadership: 66, overall: 64 };

  // Radar Chart Option
  const radarOption = {
    tooltip: { trigger: 'item' },
    legend: {
      data: ['Din Bedrift', 'Bransjesnitt', 'Nasjonalt snitt'],
      bottom: 0,
      textStyle: { color: '#64748b' }
    },
    radar: {
      indicator: [
        { name: 'Psyk. Trygghet', max: 100 },
        { name: 'Stressmestring', max: 100 },
        { name: 'Konflikthåndtering', max: 100 },
        { name: 'Ledelse', max: 100 },
        { name: 'Kultur', max: 100 }
      ],
      splitArea: { show: false },
      axisLine: { lineStyle: { color: '#e2e8f0' } }
    },
    series: [{
      type: 'radar',
      data: [
        {
          value: [companyScores.psychSafety, companyScores.stressControl, companyScores.conflictRisk, companyScores.leadership, 60],
          name: 'Din Bedrift',
          itemStyle: { color: '#4f46e5' },
          areaStyle: { opacity: 0.1 }
        },
        {
          value: [currentBenchmark.psychSafety, currentBenchmark.stressControl, currentBenchmark.conflictRisk, currentBenchmark.leadership, 68],
          name: 'Bransjesnitt',
          itemStyle: { color: '#10b981' },
          lineStyle: { type: 'dashed' }
        },
        {
          value: [nationalAverage.psychSafety, nationalAverage.stressControl, nationalAverage.conflictRisk, nationalAverage.leadership, 64],
          name: 'Nasjonalt snitt',
          itemStyle: { color: '#94a3b8' },
          lineStyle: { type: 'dotted' }
        }
      ]
    }]
  };

  // Analyse av styrker og risiko
  const analysis = useMemo(() => {
    const strengths = [];
    const risks = [];

    if (companyScores.psychSafety < currentBenchmark.psychSafety - 10) risks.push({ title: "Psykologisk Trygghet", diff: companyScores.psychSafety - currentBenchmark.psychSafety });
    if (companyScores.stressControl < currentBenchmark.stressControl - 10) risks.push({ title: "Stressmestring", diff: companyScores.stressControl - currentBenchmark.stressControl });
    if (companyScores.leadership < currentBenchmark.leadership - 5) risks.push({ title: "Lederstøtte", diff: companyScores.leadership - currentBenchmark.leadership });
    
    // Eksempel på styrke (hvis dataen hadde vært annerledes)
    if (companyScores.overall > 50) strengths.push({ title: "Sykefraværsoppfølging", desc: "Bedre enn snittet i bransjen." });

    return { strengths, risks };
  }, [industry, currentBenchmark]);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-end">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Benchmark Engine</h1>
          <p className="text-slate-500 mt-1">Sammenlign din virksomhet med bransjen og nasjonale data.</p>
        </div>
        <Button variant="secondary" icon={<SafeIcon name="Download" />}>Eksporter Benchmark-rapport</Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Filter Sidebar */}
        <Card className="lg:col-span-1 h-fit">
          <CardContent className="p-6 space-y-6">
            <div className="space-y-2">
              <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Bransje</label>
              <select 
                className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-indigo-500/20"
                value={industry}
                onChange={(e) => setIndustry(e.target.value)}
              >
                <option>Teknologi</option>
                <option>Helse</option>
                <option>Finans</option>
                <option>Offentlig</option>
              </select>
            </div>

            <div className="space-y-2">
              <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Bedriftsstørrelse</label>
              <select 
                className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-indigo-500/20"
                value={size}
                onChange={(e) => setSize(e.target.value)}
              >
                <option>Liten (1-49)</option>
                <option>Mellomstor (50-249)</option>
                <option>Stor (250+)</option>
              </select>
            </div>

            <div className="pt-4 border-t border-slate-100">
              <div className="bg-indigo-50 p-4 rounded-xl">
                <p className="text-[10px] font-bold text-indigo-600 uppercase mb-2">Datakvalitet</p>
                <p className="text-xs text-indigo-700 leading-relaxed">
                  Basert på data fra 450+ sammenlignbare virksomheter i {industry}-sektoren.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Visualization & Analysis */}
        <div className="lg:col-span-3 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="bg-white">
              <CardContent className="p-6 text-center">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Benchmark Score</p>
                <div className="text-3xl font-black text-slate-900">{companyScores.overall} / 100</div>
                <p className="text-xs text-rose-500 font-bold mt-1">-{currentBenchmark.overall - companyScores.overall}% vs bransjesnitt</p>
              </CardContent>
            </Card>
            <Card className="bg-white">
              <CardContent className="p-6 text-center">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Bransjesnitt ({industry})</p>
                <div className="text-3xl font-black text-slate-400">{currentBenchmark.overall}</div>
                <p className="text-xs text-slate-400 mt-1">Normalområde: 60-75</p>
              </CardContent>
            </Card>
            <Card className="bg-white">
              <CardContent className="p-6 text-center">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Nasjonalt Snitt</p>
                <div className="text-3xl font-black text-slate-400">{nationalAverage.overall}</div>
                <p className="text-xs text-slate-400 mt-1">Alle bransjer i Norge</p>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader title="Sammenligningsprofil" subtitle="Visuell profil mot bransje og land" />
              <CardContent>
                <div className="h-80">
                  <ReactECharts option={radarOption} style={{ height: '100%' }} />
                </div>
              </CardContent>
            </Card>

            <div className="space-y-6">
              {/* Risks Section */}
              <Card className="border-l-4 border-l-rose-500">
                <CardHeader title="Risikoområder" subtitle="Områder med signifikant avvik fra bransjen" />
                <CardContent className="space-y-4">
                  {analysis.risks.map((risk, i) => (
                    <div key={i} className="flex items-center justify-between p-3 bg-rose-50 rounded-xl">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-white rounded-lg text-rose-600 shadow-sm">
                          <SafeIcon name="AlertTriangle" />
                        </div>
                        <span className="font-bold text-slate-800 text-sm">{risk.title}</span>
                      </div>
                      <span className="text-xs font-black text-rose-600">{risk.diff}% under snitt</span>
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Strengths Section */}
              <Card className="border-l-4 border-l-emerald-500">
                <CardHeader title="Styrker" subtitle="Områder hvor dere presterer godt" />
                <CardContent>
                  {analysis.strengths.length > 0 ? analysis.strengths.map((strength, i) => (
                    <div key={i} className="flex items-center gap-4 p-3 bg-emerald-50 rounded-xl">
                      <div className="p-2 bg-white rounded-lg text-emerald-600 shadow-sm">
                        <SafeIcon name="TrendingUp" />
                      </div>
                      <div>
                        <p className="font-bold text-slate-800 text-sm">{strength.title}</p>
                        <p className="text-[10px] text-emerald-700">{strength.desc}</p>
                      </div>
                    </div>
                  )) : (
                    <p className="text-sm text-slate-400 text-center py-4">Ingen signifikante styrker identifisert mot bransjesnittet ennå.</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};